import "./reader/reader.css";

import { useMemo } from "react";

import { MessageState } from "../../../../ds";
import { renderPage, type PageFormat } from "../../../../astromark";
import { PageToc } from "./reader/PageToc";
import { PreparedHtml } from "./reader/RenderedHtml";
import { PreparedBody, prepareBody } from "./reader/readerBody";

/** Page body → sanitized HTML (`renderPage`, either format) → prepared body (heading ids, TOC, tabs, code heads). */
export function usePreparedPage(markdown: string | undefined, format?: PageFormat): PreparedBody {
    return useMemo(() => prepareBody(markdown ? renderPage({ body: markdown, format }).html : ""), [markdown, format]);
}

export interface IPageContentRendererProps {
    /** The side TOC is open: the inline one is not drawn. */
    hideToc: boolean;
    markdownContent?: string;
    format?: PageFormat;
    /** The body already prepared by the caller (it shares the TOC with the side panel); computed here otherwise. */
    body?: PreparedBody;
    className?: string;
    /** The empty page's "Edit Page" action; without it the empty state has no button. */
    emptyPageOnClick?: () => void;
}

/**
 * The reading column's body: the inline numbered TOC (when the page has more than one top-level heading and the side TOC is
 * closed) and the rendered page, styled on tokens by `.ark-prose`. An empty page shows the empty state.
 */
export function PageContentRenderer(props: IPageContentRendererProps) {
    const own = usePreparedPage(props.body ? undefined : props.markdownContent, props.format);
    const body = props.body ?? own;

    if (!props.markdownContent) {
        return (
            <MessageState
                className="ark-doc-empty"
                kind="add"
                title="This page is empty! Try add some content"
                actionLabel={props.emptyPageOnClick ? "Edit Page" : undefined}
                onAction={props.emptyPageOnClick}
            />
        );
    }

    return (
        <div className={["ark-doc-body", props.className].filter(Boolean).join(" ")}>
            {!props.hideToc && body.toc.length > 1 ? (
                <div className="ark-doc-inline-toc">
                    <span className="ow-eyebrow">On this page</span>
                    <PageToc entries={body.toc} variant="inline" />
                </div>
            ) : null}
            <PreparedHtml body={body} className="ark-prose" />
        </div>
    );
}
